import { UserManagementUserControls } from './components/user-management-user-controls.tsx'
import { selectManagementInput } from '../../redux/slices/management-input.slice.ts'
import { useAppSelector } from '../../redux/hooks.ts'
import { useListUsersQuery } from '../../apis/user-management/user-management-api.api.ts'
import { UserManagementTable } from './components/user-management-table.tsx'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { UserManagementFormFieldTypes } from './components/user-management-form/user-management-form.tsx'
import Form from 'antd/es/form/index'
import { ActionUserModal } from './components/user-modal/user-action/action-user-modal.tsx'
import { InviteUserModal } from './components/user-modal/user-invite-modal/user-invite-modal.tsx'
import { UserBlockLeaveFormFieldTypes } from './components/user-block-leave-form/user-block-leave-form.tsx'
import {
  BlockLeaveTab,
  UserBlockLeaveCombinedModal,
} from './components/user-modal/user-block-leave/user-block-leave-combined-modal.tsx'
import { UserChangeLiquidateModal } from './components/user-modal/user-block-leave/user-change-liquidate-modal.tsx'
import { UserWithdrawBlockLeaveModal } from './components/user-modal/user-block-leave/user-withdraw-block-leave-modal.tsx'

export const UserManagement = () => {
  const [showUserModal, setShowUserModal] = useState<boolean>(false)
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false)
  const [showBlockLeaveCombinedModal, setShowBlockLeaveCombinedModal] =
    useState<boolean>(false)
  const [blockLeaveActiveTab, setBlockLeaveActiveTab] =
    useState<BlockLeaveTab>('apply')
  const [showChangeLiquidateModal, setShowChangeLiquidateModal] =
    useState<boolean>(false)
  const [showWithdrawBlockLeaveModal, setShowWithdrawBlockLeaveModal] =
    useState<boolean>(false)
  const [inviteUserId, setInviteUserId] = useState<string | null>(null)
  const [form] = Form.useForm<UserManagementFormFieldTypes>()
  const [blockLeaveUserId, setBlockLeaveUserId] = useState<string | null>(null)
  const [blockLeaveId, setBlockLeaveId] = useState<string | null>(null)
  const [changeLiquidateValue, setChangeLiquidateValue] =
    useState<boolean>(false)
  const [blockLeaveForm] = Form.useForm<UserBlockLeaveFormFieldTypes>()
  const managementInput = useAppSelector(selectManagementInput)
  const { accountCategory } = managementInput
  const [searchFullName, setSearchFullName] = useState<string>('')
  const [actionUserId, setActionUserId] = useState<string | null>(null) // <-- add this state

  const { data, isFetching, isLoading } = useListUsersQuery({
    page: 1,
    accountType: managementInput.accountType,
    status: managementInput.accountStatus,
    internalRole: managementInput.accountInternalRole,
    firmId: managementInput.accountFirm,
    firmRole: managementInput.accountFirmRole,
    groupRole: managementInput.accountGroupRole,
    ...(managementInput.accountIsTrial ? { isTrial: true } : {}),
  })

  useEffect(() => {
    form.setFieldValue('accountType', managementInput.accountType)
  }, [form, managementInput.accountType])

  const handleCloseModal = () => {
    form.resetFields()
    setShowUserModal(false)
    setActionUserId(null)
  }

  const handleEditUser = (id: string) => {
    setActionUserId(id)
    setShowUserModal(true)
  }

  const filteredUserList = useMemo(() => {
    const users = data?.data.users ?? []
    return users.filter((user) => {
      const matchesCategory =
        !accountCategory || user?.group?.name === accountCategory
      const matchesSearch =
        !searchFullName ||
        user.fullName?.toLowerCase().includes(searchFullName.toLowerCase())
      return matchesCategory && matchesSearch
    })
  }, [data, accountCategory, searchFullName])

  const handleInviteUser = (id: string) => {
    setInviteUserId(id)
    setShowInviteModal(true)
  }

  const handleOpenBlockLeave = useCallback((id: string, tab: BlockLeaveTab) => {
    setBlockLeaveUserId(id)
    setBlockLeaveActiveTab(tab)
    setShowBlockLeaveCombinedModal(true)
  }, [])

  const handleCloseBlockLeave = useCallback(() => {
    setShowBlockLeaveCombinedModal(false)
    blockLeaveForm.resetFields()
  }, [blockLeaveForm])

  const handleChangeLiquidate = (blockLeaveId: string, value: boolean) => {
    setBlockLeaveId(blockLeaveId)
    setChangeLiquidateValue(value)
    setShowChangeLiquidateModal(true)
  }

  const handleWithdraw = (blockLeaveId: string) => {
    setBlockLeaveId(blockLeaveId)
    setShowWithdrawBlockLeaveModal(true)
  }

  return (
    <>
      <UserManagementUserControls
        onCreateUser={() => setShowUserModal(true)}
        searchFullName={searchFullName}
        setSearchFullName={setSearchFullName}
      />
      {/* Absolute inner box so the grid's 100% height resolves inside a flex-grown parent */}
      <div style={{ flex: '1 1 0', minHeight: 0, position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          <UserManagementTable
            userList={filteredUserList}
            isLoading={isLoading}
            isFetching={isFetching}
            onEditUser={handleEditUser}
            onInviteUser={handleInviteUser}
            onOpenBlockLeave={handleOpenBlockLeave}
            height={'100%'}
          />
        </div>
      </div>
      <ActionUserModal
        showActionModal={showUserModal}
        form={form}
        handleCloseModal={handleCloseModal}
        editUserId={actionUserId}
        setEditUserId={setActionUserId}
      />
      <InviteUserModal
        inviteUserId={inviteUserId}
        showInviteModal={showInviteModal}
        setShowInviteModal={setShowInviteModal}
        onCancel={() => setShowInviteModal(false)}
      />
      <UserBlockLeaveCombinedModal
        open={showBlockLeaveCombinedModal}
        userId={blockLeaveUserId}
        activeTab={blockLeaveActiveTab}
        onClose={handleCloseBlockLeave}
        onTabChange={setBlockLeaveActiveTab}
        form={blockLeaveForm}
        handleChangeLiquidate={handleChangeLiquidate}
        handleWithdraw={handleWithdraw}
      />
      <UserChangeLiquidateModal
        handleCancel={() => setShowChangeLiquidateModal(false)}
        showChangeLiquidateModal={showChangeLiquidateModal}
        blockLeaveId={blockLeaveId}
        liquidateValue={changeLiquidateValue}
      />
      <UserWithdrawBlockLeaveModal
        blockLeaveId={blockLeaveId}
        showWithdrawBlockLeaveModal={showWithdrawBlockLeaveModal}
        handleCancel={() => setShowWithdrawBlockLeaveModal(false)}
      />
    </>
  )
}
